import React, { useState, useEffect } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { BrandGradient } from '../components/common/PFILogo';
import { addSampleSuppliers, addSampleCustomers, checkExistingData } from '../utils/sampleData';
import TaskCategorySettings from '../components/TaskCategorySettings';
import { 
  CogIcon,
  PlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BuildingStorefrontIcon,
  UserIcon,
  TagIcon
} from '@heroicons/react/24/outline';

export default function AdminPage() {
  const [loading, setLoading] = useState(false);
  const [dataStatus, setDataStatus] = useState({ suppliers: 0, customers: 0 });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [activeTab, setActiveTab] = useState('data');

  useEffect(() => {
    checkData();
  }, []);

  const checkData = async () => {
    const status = await checkExistingData();
    setDataStatus(status);
  };

  const handleAddSampleSuppliers = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const count = await addSampleSuppliers();
      setMessage(`✅ Successfully added ${count} sample suppliers!`);
      setMessageType('success');
      
      // Refresh data status
      checkData();
      
    } catch (error) {
      setMessage(`❌ Error adding suppliers: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSampleCustomers = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const count = await addSampleCustomers();
      setMessage(`✅ Successfully added ${count} sample customers!`);
      setMessageType('success');
      
      // Refresh data status
      checkData();
      
    } catch (error) {
      setMessage(`❌ Error adding customers: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAllSampleData = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const [suppliersCount, customersCount] = await Promise.all([
        addSampleSuppliers(),
        addSampleCustomers()
      ]);
      
      setMessage(`✅ Successfully added ${suppliersCount} suppliers and ${customersCount} customers!`);
      setMessageType('success');
      
      // Refresh data status
      checkData();
      
    } catch (error) {
      setMessage(`❌ Error adding sample data: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <BrandGradient className="rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-start">
            <CogIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">Admin Panel</h1>
              <p className="text-pink-100 text-xl">
                Setup and manage your PFI Connect system
              </p>
            </div>
          </div>
        </BrandGradient>

        {/* Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('data')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'data'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <BuildingStorefrontIcon className="w-5 h-5 inline mr-2" />
                Sample Data
              </button>
              <button
                onClick={() => setActiveTab('categories')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'categories'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <TagIcon className="w-5 h-5 inline mr-2" />
                Task Categories
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'data' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Data Status */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Current Data Status</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <BuildingStorefrontIcon className="w-5 h-5 text-blue-600 mr-3" />
                    <span className="text-gray-700">Suppliers</span>
                  </div>
                  <span className="font-semibold text-blue-600">{dataStatus.suppliers}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <UserIcon className="w-5 h-5 text-green-600 mr-3" />
                    <span className="text-gray-700">Customers</span>
                  </div>
                  <span className="font-semibold text-green-600">{dataStatus.customers}</span>
                </div>
              </div>

              <button
                onClick={checkData}
                className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Refresh Status
              </button>
            </div>

            {/* Sample Data Actions */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Sample Data Setup</h2>
              
              <div className="space-y-6">
                {dataStatus.suppliers === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                      <div className="text-yellow-800 text-sm">
                        <p className="font-semibold">No suppliers found!</p>
                        <p>You need suppliers in the system to assign them to tasks. Add sample suppliers to get started.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={handleAddSampleSuppliers}
                    disabled={loading}
                    className="flex items-center justify-center px-6 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <BuildingStorefrontIcon className="w-5 h-5 mr-2" />
                    Add Sample Suppliers
                  </button>

                  <button
                    onClick={handleAddSampleCustomers}
                    disabled={loading}
                    className="flex items-center justify-center px-6 py-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <UserIcon className="w-5 h-5 mr-2" />
                    Add Sample Customers
                  </button>
                </div>

                <button
                  onClick={handleAddAllSampleData}
                  disabled={loading}
                  className="w-full flex items-center justify-center px-6 py-4 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-50"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  {loading ? 'Adding Data...' : 'Add All Sample Data'}
                </button>

                {/* Sample Data Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Sample Data Includes:</h3>
                  <div className="text-blue-800 text-sm space-y-1">
                    <p>• 5 Suppliers (upholstery, fabric, delivery, furniture, installation)</p>
                    <p>• 3 Customers with different project types</p>
                    <p>• Contact details, specialties, and categories</p>
                    <p>• Ready for immediate task assignment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'categories' && (
          <TaskCategorySettings />
        )}

        {/* Status Message */}
        {message && (
          <div className={`mt-6 p-4 rounded-lg ${
            messageType === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-center">
              {messageType === 'success' ? (
                <CheckCircleIcon className="w-5 h-5 mr-2" />
              ) : (
                <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
              )}
              <span>{message}</span>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}